/**
 * Applying lexicon matches to a document under review (VE-24948 / U5).
 *
 * This file implements **D13** — the decision deferred from inception on how
 * newly produced lexicon detections merge with work the reviewer has already
 * done. The governing acceptance criterion (US-U5-6) is:
 *
 *   "Applying a lexicon does not discard my existing manual redactions or
 *    reset my decisions."
 *
 * Three rules, and the reasoning for each, because the tempting simplification
 * of any one of them reintroduces a defect:
 *
 *   M1  PURELY ADDITIVE TO MANUAL WORK. Applying never modifies, moves, deletes
 *       or un-redacts an existing `Redaction`, and never clears
 *       `redactedMatchIds` on an existing detection. This is the literal AC and
 *       is non-negotiable.
 *
 *   M2  FOLD PER OCCURRENCE: exact phrase equality AND same page AND geometric
 *       overlap with the redaction's own box. A `keyword`-origin redaction
 *       covers ONE occurrence (the DOM walker creates one redaction per
 *       occurrence it redacts), so the fold must be occurrence-level too.
 *       Keyed on the phrase alone, the fold suppressed EVERY match of that
 *       text document-wide — one hand-redacted "John Smith" on page 3 silently
 *       swallowed the other eleven occurrences, with no detection card and a
 *       match count of 0 (review-gate finding 1). Un-redacted keyword entries
 *       (`redacted: false`) never fold: nothing is burned in for them, so
 *       adopting one would suppress a candidate while leaving the text exposed.
 *
 *       Geometry-overlap as the SOLE trigger was considered and REJECTED — a
 *       coverage tolerance will be wrong at some zoom, rotation or glyph-pad
 *       boundary. Used here only to NARROW a text-equal fold, its error
 *       direction is safe: a missed overlap produces a duplicate candidate
 *       card over an already-redacted box (cosmetic), never a suppressed
 *       un-redacted match. Both boxes come from the same MuPDF glyph geometry
 *       (`glyphSpanGeometry`), so in practice they coincide.
 *
 *   M3  DISMISSALS SURVIVE. A match whose id is already in `deletedMatchIds` is
 *       not re-added. This is what makes re-application idempotent, and it is
 *       why match ids MUST be deterministic — see `lexiconMatchId`.
 *
 *   M4  THE ENTRY'S REDACTION CODE IS STAMPED ON CREATION ONLY (`U5-FR8`,
 *       BR-15). An entry may reference a redaction code, and until this rule
 *       existed that reference went nowhere: the code was stored on the entry,
 *       shown back in the settings drawer, and then dropped at apply, so a
 *       lexicon that named a code produced uncoded boxes and an empty code
 *       column in the disclosure report. The card is where it belongs — one
 *       card per (lexicon, entry) is exactly the granularity a code has.
 *
 *       ON CREATION ONLY, because `redactionCode` is triage state the reviewer
 *       owns: they can assign a different code or clear it outright. Stamping
 *       on every merge would let the next batch of page text — which arrives
 *       on its own schedule, with no user action — silently undo that. So an
 *       existing card keeps whatever code it carries, including none, and M1
 *       holds for the code the same way it holds for everything else.
 *
 *       An entry whose code has since been deleted resolves to nothing and
 *       applies uncoded rather than failing (BR-15): the match still has to be
 *       found, and an uncoded redaction is recoverable while a missed one is
 *       not.
 *
 * Everything here is pure, so the whole merge matrix is testable without a
 * store, a worker or a document.
 *
 * The merge must stay linear in matches: it runs on the main thread, outside
 * the matching budgets (see `WorkingCard`).
 */

import { LexiconMatchSpan } from './lexiconMatchWorker';
import {
  aabbOverlaps,
  deriveDetectionRedaction,
  stripItemSeams,
} from './helpers';
// A VIEW derivation, kept out of this merge module on purpose — see its header.
import { countLexiconMatches } from './lexiconCounts';
import {
  GlyphItem,
  itemCharRanges,
  rowBoxesForSpan,
} from './glyphSpanGeometry';
import {
  Detection,
  LexiconSourcedDetection,
  PiiOccurrence,
  Redaction,
  RedactionCodeAssignment,
} from './models';
import {
  Lexicon,
  LexiconEntry,
  LexiconEntrySnapshot,
} from '@redact-modules/lexicons/models';

/**
 * Deterministic id for one lexicon match.
 *
 * MUST NOT be random. Triage state — `redactedMatchIds` and `deletedMatchIds` —
 * is keyed by match id, so a fresh id per run would resurrect every dismissed
 * match on re-application (M3) and lose every redaction selection. That is the
 * same defect class as "resetting my decisions", which US-U5-6 forbids.
 *
 * Deterministic ACROSS TEXT EXTRACTORS, which a character offset is not.
 *
 * The id was `entryId:page:index:length`, resting on "pdfjs text extraction is
 * stable for a given PDF". That is true of one extractor, and this app has two
 * writers of `session.pageTexts`: the MuPDF pre-pass (one item per GLYPH, so a
 * seam between every glyph) and the render-path backfill in `DocumentPage`
 * (pdfjs multi-char items). A page that renders before its pre-pass chunk lands
 * — the normal case for page 1 — gets pdfjs text first and MuPDF text after,
 * and the offsets differ by roughly 2x. Every id changed, so a dismissed match
 * came back and its rects were appended to the same occurrence, giving one
 * physical hit two entries (review finding 4).
 *
 * Keyed on the ORDINAL instead: the Nth match of this entry on this page.
 * Extractor-independent, and stable under an offset shift. Deliberately NOT
 * derived from the matched text — an id is persisted, and a term must never
 * reach an artifact that does not already carry it (NFR-S7).
 *
 * Keyed on `entryId` rather than the term so that editing an entry's OPTIONS
 * (case sensitivity, whole-word) does not alias its matches onto a different
 * entry's.
 */
export function lexiconMatchId(span: {
  readonly entryId: string;
  readonly page: number;
  /** 0-based position of this match among that entry's matches on that page. */
  readonly ordinal: number;
}): string {
  return `lex:${span.entryId}:${span.page}:${span.ordinal}`;
}

/** Stable id for the detection card that groups one entry's matches. */
export function lexiconDetectionId(lexiconId: string, entryId: string): string {
  return `lexicon-detection:${lexiconId}:${entryId}`;
}

export interface LexiconMatchWithText extends LexiconMatchSpan {
  /**
   * The matched text, taken from the page.
   *
   * Needed for the M2 fold (exact phrase equality) and for the detection label.
   * It is NOT the entry's term: a regex entry's term is a pattern, and even a
   * literal term can match a different whitespace run than it contains.
   */
  readonly matchedText: string;
  /** Glyph rectangles for this match, resolved on the main thread. */
  readonly rects: PiiOccurrence['rects'];
}

/**
 * Resolves worker match spans into matched text plus exact-glyph geometry.
 *
 * The worker deliberately returns CHARACTER OFFSETS only — it has no pdfjs and
 * no geometry model (ADR-002). This is where offsets become rectangles, on the
 * main thread where `pageTextItems` lives.
 *
 * Geometry comes from `rowBoxesForSpan`, the same source phrase redaction uses,
 * so a lexicon redaction and a hand-typed keyword redaction of the same text
 * produce identical boxes. Critically it is NOT the keyword-search saga's
 * mapping: that produces a padded union box documented as "only accurate enough
 * for the highlight overlay", and a union across a wrapped match would redact
 * unrelated text between the rows.
 *
 * A span that resolves to no boxes is dropped rather than emitted with an empty
 * rect list: a detection you cannot see or burn in is worse than one that never
 * appeared, because it would count toward the match total.
 */
export function resolveLexiconMatchGeometry(
  spans: readonly LexiconMatchSpan[],
  pageText: string,
  items: ReadonlyArray<GlyphItem | undefined>
): LexiconMatchWithText[] {
  const ranges = itemCharRanges(items);
  const resolved: LexiconMatchWithText[] = [];

  for (const span of spans) {
    const startIdx = span.index;
    const endIdx = span.index + span.length;
    const rects = rowBoxesForSpan({ startIdx, endIdx }, items, ranges);
    if (rects.length === 0) {
      continue;
    }
    resolved.push({
      ...span,
      // Seams are internal markers for item joins, not text the user sees. They
      // must be stripped before this string is compared for the M2 fold or used
      // as a label — the keyword saga strips them for the same reason.
      matchedText: stripItemSeams(pageText.slice(startIdx, endIdx)),
      rects,
    });
  }

  return resolved;
}

export interface MergeLexiconMatchesArgs {
  /** Matches produced by this application, with text and geometry resolved. */
  readonly matches: readonly LexiconMatchWithText[];
  /** The lexicons that produced them, for snapshotting. */
  readonly lexicons: readonly Lexicon[];
  /** The session's current detections — PII and lexicon alike. */
  readonly existingDetections: readonly Detection[];
  /** The session's current redactions. Used by M2, never modified (M1). */
  readonly existingRedactions: readonly Redaction[];
  /**
   * The org's redaction codes by id, for M4.
   *
   * OPTIONAL, and absent means "stamp nothing" rather than "fail". BR-15 says
   * a code that cannot be resolved must not block matching, and the same
   * argument covers not having the codes to hand: a caller that could not load
   * them still has matches to place, and refusing to place them would trade a
   * missing label for a missing redaction.
   */
  readonly redactionCodes?: ReadonlyMap<string, RedactionCodeAssignment>;
  /**
   * Lexicons whose matches are redacted on arrival instead of being offered
   * for review (VE-24948).
   *
   * Applies to CARDS THIS RUN CREATES ONLY. A card that already existed
   * carries a reviewer's decisions, and re-applying must not overwrite them —
   * the same rule `redactionCode` follows, and the reason both are seeded
   * inside the `existing ??` default rather than after it.
   */
  readonly autoRedactLexiconIds?: ReadonlySet<string>;
}

export interface MergeLexiconMatchesResult {
  /**
   * The detection list after the merge.
   *
   * Existing entries are carried through UNCHANGED — M1 is enforced by
   * construction here, not by a later check.
   */
  readonly detections: readonly Detection[];
  /** Ids of existing keyword redactions adopted by M2. */
  readonly foldedRedactionIds: readonly string[];
  /**
   * Entry ids whose matches could not be attributed to any supplied lexicon.
   *
   * Non-empty means the match set and the lexicon set disagree — the caller
   * must surface it rather than report a match count it cannot show.
   */
  readonly unattributedEntryIds: readonly string[];
  /** How many matches this merge actually put on a card. */
  readonly representedMatchCount: number;
  /**
   * How many matches are on the document AFTER this merge — the total, not this
   * run's delta.
   *
   * These are different numbers and the UI needs the total. Every open runs the
   * apply more than once (page text arrives in chunks, and the pre-pass fires
   * its own completion signal), and re-runs are idempotent BY DESIGN, so the
   * second run legitimately adds nothing and `representedMatchCount` is 0.
   * Reporting that as the answer made a document with matches announce "No
   * matches found", and the reverse ordering made one announce a count with no
   * cards to back it. Derived from the merged detections with the same rule the
   * detections list counts by, so the panel's number and the visible cards
   * cannot disagree.
   */
  readonly documentMatchCount: number;
  /**
   * Match ids NOT added because M2 adopted an existing keyword redaction of the
   * same phrase. Reported so a caller can keep its "matches found" count and
   * the rows it shows in agreement — an adopted match is not a new finding to
   * triage.
   */
  readonly foldedMatchIds: readonly string[];
  /** Match ids skipped because the reviewer had dismissed them (M3). */
  readonly skippedDismissedMatchIds: readonly string[];
  /** Match ids that were already present, so re-apply is a no-op. */
  readonly alreadyPresentMatchIds: readonly string[];
  /**
   * Match ids THIS RUN auto-redacted.
   *
   * Named apart from the card's `autoRedactedMatchIds` because the two have
   * opposite lifetimes and sharing a spelling is what produced the bug this
   * field fixes. The card's is DURABLE — it records how a match first came to
   * be redacted and survives the reviewer un-redacting it, the lexicon being
   * removed, and the document being reopened. This one is per-call. Summing
   * the durable one answers "what has this document ever auto-redacted"; the
   * caller raising the summary dialog is asking what this run just did.
   */
  readonly runAutoRedactedMatchIds: readonly string[];
}

function snapshotFor(
  lexicon: Lexicon,
  entry: LexiconEntry
): LexiconEntrySnapshot {
  return {
    lexiconId: lexicon.id,
    // Captured AS AT application time and never re-resolved on read — the
    // mechanism behind U5-FR2.
    lexiconName: lexicon.name,
    entryId: entry.entryId,
    term: entry.term,
    type: entry.type,
  };
}

/**
 * Which lexicon/entry each match can come from, resolved from the lexicon set.
 *
 * Returns the ENTRY, not just its lexicon's id: M4 needs `redactionCodeId`,
 * and having the caller re-find the entry to read it would be a second lookup
 * that can disagree with this one.
 *
 * Built once per merge; first occurrence wins.
 */
function indexEntries(
  lexicons: readonly Lexicon[]
): Map<string, { lexicon: Lexicon; entry: LexiconEntry }> {
  const index = new Map<string, { lexicon: Lexicon; entry: LexiconEntry }>();
  for (const lexicon of lexicons) {
    for (const entry of lexicon.entries) {
      if (!index.has(entry.entryId)) {
        index.set(entry.entryId, { lexicon, entry });
      }
    }
  }
  return index;
}

/**
 * A card's mutable state during `mergeLexiconMatches`, materialised once after
 * the loop. Rebuilding the card per match was O(M²) — 6.7 s for 45k matches
 * (VE-29502).
 */
interface WorkingCard {
  /** The session's card, if there was one. Never mutated. */
  readonly existing: LexiconSourcedDetection | undefined;
  readonly snapshot: LexiconEntrySnapshot;
  readonly entryCode: RedactionCodeAssignment | undefined;
  readonly autoRedact: boolean;
  /** Every match id already on the card, including this run's. */
  readonly matchIds: Set<string>;
  readonly deletedMatchIds: ReadonlySet<string>;
  /** Copy of the occurrence list; entries are replaced, never mutated. */
  occurrences: PiiOccurrence[] | null;
  /** First occurrence index per page, matching the old `findIndex`. */
  pageIndex: Map<number, number> | null;
  /** Occurrence indexes already copied by this run, safe to push onto. */
  readonly owned: Set<number>;
  /** Match ids this run added, in order. */
  readonly added: string[];
}

/**
 * Which lexicons the abandoned entries came from, by NAME.
 *
 * BR-47 requires a failing entry be reported rather than silently dropped, and
 * the panel used to discharge that by printing the raw `entryId`s. Those ids
 * are rendered nowhere else in the product — not on the Lexicons settings page,
 * not in the drawer, not in the audit trail — so the sentence handed the
 * reviewer a row of UUIDs they had no way to resolve into entries, and the
 * longer the failure the longer the wall of hex.
 *
 * The lexicon NAME is the identifier that is both resolvable (it is the row the
 * admin opens to fix the entry) and permitted: NFR-S7 / SEC-R3 keep entry TERMS
 * off every surface a document reader can reach, which is why this deliberately
 * does not return them, but names are what the panel is already listing two
 * sections below.
 *
 * Sorted and de-duplicated, so the sentence is stable across runs rather than
 * ordered by whichever entry happened to blow its budget first.
 *
 * An entry that resolves to no lexicon at all is skipped, not guessed at: that
 * is the `unattributedEntryIds` case, where the match set and the lexicon set
 * disagree, and there is no name to give. The caller therefore has to handle a
 * non-zero count with an empty name list.
 */
/**
 * Which lexicon owns each entry, as one map over the whole loaded set.
 *
 * Exists because "this RUN did not finish" and "this LEXICON did not finish"
 * are different claims, and the durable record only ever carried the first.
 * One entry with a runaway pattern marked every lexicon in the run incomplete,
 * so a reviewer with four lexicons on a document saw four warnings, three of
 * them about work that had in fact completed — and the one that meant
 * something was indistinguishable from the three that did not.
 *
 * Built once and read per entry, rather than scanning every lexicon's entries
 * for every id: an apply can abandon or fail to place hundreds of matches, and
 * the per-id search was quadratic in the size of the library.
 *
 * An entry that appears in no lexicon is simply absent from the map — that is
 * the `unattributedEntryIds` case, where the match set and the lexicon set
 * disagree, and there is no owner to charge. Callers keep those run-wide
 * rather than blaming a lexicon at random.
 */
export function lexiconIdByEntryId(
  lexicons: readonly Lexicon[]
): Map<string, string> {
  const owner = new Map<string, string>();
  for (const lexicon of lexicons) {
    for (const entry of lexicon.entries) {
      owner.set(entry.entryId, lexicon.id);
    }
  }
  return owner;
}

export function lexiconNamesForEntries(
  lexicons: readonly Lexicon[],
  entryIds: readonly string[]
): string[] {
  const owner = lexiconIdByEntryId(lexicons);
  const nameOf = new Map(lexicons.map((l) => [l.id, l.name]));
  const names = new Set<string>();
  for (const entryId of entryIds) {
    const name = nameOf.get(owner.get(entryId) ?? '');
    if (name !== undefined) {
      names.add(name);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

/**
 * Merges freshly produced lexicon matches into the session's detections.
 *
 * Grouped one detection card per (lexicon, entry), matching how PII cards group
 * per phrase, so the existing tri-state derivation and per-match controls work
 * unchanged.
 */
export function mergeLexiconMatches(
  args: MergeLexiconMatchesArgs
): MergeLexiconMatchesResult {
  const {
    matches,
    lexicons,
    existingDetections,
    existingRedactions,
    redactionCodes,
    autoRedactLexiconIds,
  } = args;

  // M2 — existing KEYWORD redactions, grouped by phrase. Deliberately not
  // manual or text-select redactions: `keyword` is the origin created by
  // searching for a phrase, which is the case where the reviewer has already
  // done by hand what the lexicon is about to do. Grouped (not a phrase → one
  // id map) because each keyword redaction covers ONE occurrence — the fold
  // below must find the redaction covering THIS match, not any of the phrase.
  // `redacted === false` entries are excluded: they burn nothing in, so
  // adopting one would suppress a candidate over still-exposed text.
  /** Cards this run auto-redacted, finished off after the loop. */
  const autoRedactedCardIds = new Set<string>();
  const keywordRedactionsByPhrase = new Map<string, Redaction[]>();
  for (const redaction of existingRedactions) {
    if (
      redaction.origin === 'keyword' &&
      redaction.phrase &&
      redaction.redacted !== false
    ) {
      const list = keywordRedactionsByPhrase.get(redaction.phrase) ?? [];
      list.push(redaction);
      keywordRedactionsByPhrase.set(redaction.phrase, list);
    }
  }

  // Existing lexicon cards, so re-application updates rather than duplicates.
  const existingLexiconCards = new Map<string, LexiconSourcedDetection>();
  for (const detection of existingDetections) {
    if (detection.source === 'lexicon') {
      existingLexiconCards.set(detection.id, detection);
    }
  }

  const foldedRedactionIds: string[] = [];
  const foldedRedactionIdSet = new Set<string>();
  /** Matches adopted by M2 — found, but already redacted by hand. */
  const foldedMatchIds: string[] = [];
  /** Entries whose matches could not be attributed to a lexicon at all. */
  const unattributedEntryIds = new Set<string>();
  /** Matches actually represented on a card by this merge. */
  let representedMatchCount = 0;
  const skippedDismissedMatchIds: string[] = [];
  const alreadyPresentMatchIds: string[] = [];

  const entryIndex = indexEntries(lexicons);
  /** Every card a match has reached, keyed by detection id. */
  const working = new Map<string, WorkingCard>();
  /** Cards that gained a match, in the order they first did. */
  const changedCardIds: string[] = [];

  for (const match of matches) {
    const located = entryIndex.get(match.entryId);
    if (!located) {
      // The entry is not in the supplied lexicon set — nothing to snapshot, so
      // the match cannot satisfy the U5-FR2 obligation. Dropping it is correct:
      // a detection without a snapshot would dangle once the lexicon changed.
      //
      // But dropping it SILENTLY is not. This branch and the one below were
      // bare `continue`s, so a run where every match failed to attribute
      // produced no cards while the caller still reported the full match count
      // — "15 matches found" over an empty list, with nothing anywhere saying
      // why. Reported now, and the caller counts only what it can show.
      unattributedEntryIds.add(match.entryId);
      continue;
    }

    const detectionId = lexiconDetectionId(located.lexicon.id, match.entryId);
    let card = working.get(detectionId);
    if (!card) {
      const existing = existingLexiconCards.get(detectionId);
      const matchIds = new Set<string>();
      for (const o of existing?.occurrences ?? []) {
        for (const id of o.matchIds ?? []) {
          matchIds.add(id);
        }
      }
      card = {
        existing,
        snapshot: snapshotFor(located.lexicon, located.entry),
        // M4 — the entry's code, resolved once, and ONLY for a card being
        // created. An entry with no code, or one whose code has been deleted
        // since, yields `undefined` and the card is created uncoded (BR-15).
        entryCode: located.entry.redactionCodeId
          ? redactionCodes?.get(located.entry.redactionCodeId)
          : undefined,
        // AUTO REDACT only for cards this run creates (not in the session).
        autoRedact:
          autoRedactLexiconIds?.has(located.lexicon.id) === true && !existing,
        matchIds,
        deletedMatchIds: new Set(existing?.deletedMatchIds ?? []),
        occurrences: null,
        pageIndex: null,
        owned: new Set(),
        added: [],
      };
      working.set(detectionId, card);
    }

    /**
     * The ordinal comes FROM THE MATCHER, and is not recounted here.
     *
     * It used to be a running counter over this loop — which runs after
     * `resolveMatchGeometry` has dropped every span it could not place, so it
     * counted survivors rather than matches. Two runs that resolved different
     * subsets gave the same occurrence different ids, and a dismissal recorded
     * against one run landed on a different piece of text in the next.
     *
     * Counting it where the match is FOUND makes it a property of the page's
     * text, which is what `lexiconMatchId` documents it as: the Nth match of
     * this entry on this page, extractor-independent and stable under an
     * offset shift.
     */
    const matchId = lexiconMatchId({
      entryId: match.entryId,
      page: match.page,
      ordinal: match.ordinal,
    });

    // M3 — a dismissed match is never re-added.
    if (card.deletedMatchIds.has(matchId)) {
      skippedDismissedMatchIds.push(matchId);
      continue;
    }

    // Idempotence: the same match arriving again is not a second occurrence.
    if (card.matchIds.has(matchId)) {
      alreadyPresentMatchIds.push(matchId);
      continue;
    }

    // M2 — fold onto the existing keyword redaction covering THIS OCCURRENCE:
    // identical phrase, same page, and the redaction's box overlaps one of the
    // match's rects. The reviewer has already done by hand what the lexicon is
    // about to do HERE, so ADOPT that redaction and do not add a second
    // overlapping detection. Every other occurrence of the same text still
    // produces a match — folding on the phrase alone suppressed them all
    // (review-gate finding 1). The `continue` is the fold (review finding 8).
    const foldCandidates = keywordRedactionsByPhrase.get(match.matchedText);
    const folded = foldCandidates?.find(
      (r) =>
        (r.appliesToAllPages === true || r.page === match.page) &&
        match.rects.some((rect) => aabbOverlaps(rect, r.geometry))
    );
    if (folded) {
      if (!foldedRedactionIdSet.has(folded.id)) {
        foldedRedactionIdSet.add(folded.id);
        foldedRedactionIds.push(folded.id);
      }
      foldedMatchIds.push(matchId);
      continue;
    }

    // ONE MATCH ID PER RECT. This is a contract, not a convenience:
    // `DetectionMaskOverlay` reads `occ.matchIds?.[idx]` alongside
    // `occ.rects[idx]`, so a match that WRAPS ACROSS ROWS (several rects) must
    // repeat its id on each of them. Pushing one id for several rects shifts
    // every later rect onto the wrong match — the second row of a wrapped match
    // would read another match's redacted/dismissed state.
    const rectIds = match.rects.map(() => matchId);

    // Copy lazily, so an untouched card keeps its identity.
    if (!card.occurrences || !card.pageIndex) {
      const occurrences = [...(card.existing?.occurrences ?? [])];
      const pageIndex = new Map<number, number>();
      occurrences.forEach((o, i) => {
        if (!pageIndex.has(o.page)) {
          pageIndex.set(o.page, i);
        }
      });
      card.occurrences = occurrences;
      card.pageIndex = pageIndex;
    }

    // Merge into the same page's occurrence when one exists, mirroring how PII
    // folds several matches of a phrase onto one page entry.
    const samePageIndex = card.pageIndex.get(match.page);
    const samePage =
      samePageIndex === undefined ? undefined : card.occurrences[samePageIndex];
    if (samePageIndex !== undefined && samePage) {
      if (card.owned.has(samePageIndex)) {
        // Already our copy: append.
        samePage.rects.push(...match.rects);
        samePage.matchIds!.push(...rectIds);
      } else {
        // The session's occurrence: copy first (M1).
        card.occurrences[samePageIndex] = {
          ...samePage,
          rects: [...samePage.rects, ...match.rects],
          matchIds: [...(samePage.matchIds ?? []), ...rectIds],
        };
        card.owned.add(samePageIndex);
      }
    } else {
      card.pageIndex.set(match.page, card.occurrences.length);
      card.owned.add(card.occurrences.length);
      card.occurrences.push({
        page: match.page,
        rects: [...match.rects],
        id: matchId,
        matchIds: rectIds,
      });
    }

    if (card.added.length === 0) {
      changedCardIds.push(detectionId);
    }
    card.added.push(matchId);
    card.matchIds.add(matchId);
    if (card.autoRedact) {
      autoRedactedCardIds.add(detectionId);
    }
    representedMatchCount += 1;
  }

  /** New or updated cards, keyed by detection id. */
  const cards = new Map<string, LexiconSourcedDetection>();
  for (const detectionId of changedCardIds) {
    const card = working.get(detectionId)!;
    const { existing, snapshot, entryCode } = card;
    cards.set(detectionId, {
      // Spreading `existing` first preserves the reviewer's triage state —
      // `redactedMatchIds`, `deletedMatchIds`, `checked`, `redactionCode`,
      // `addedToRedactions`. This is where M1 is actually enforced for
      // detections, so do not reorder it.
      ...(existing ?? {
        source: 'lexicon' as const,
        id: detectionId,
        label: snapshot.term,
        occurrences: [],
        checked: false as boolean | 'partial',
        expanded: false,
        // Inside the `existing ??` default ON PURPOSE — this is the one place
        // the code can be stamped without overwriting a reviewer's own
        // assignment or their deliberate removal of one (M4). Spread as a
        // conditional so the key is absent rather than explicitly undefined:
        // the card is persisted, and `redactionCode: undefined` and no
        // `redactionCode` are the same state written two different ways.
        ...(entryCode ? { redactionCode: entryCode } : {}),
      }),
      source: 'lexicon',
      id: detectionId,
      label: existing?.label ?? snapshot.term,
      // Refreshed each application so a renamed lexicon shows its new name on
      // NEWLY applied matches, while already-applied cards keep the snapshot
      // they were created with (they are not re-created here).
      lexicon: existing?.lexicon ?? snapshot,
      // One stable sort; same result as sorting after every match.
      occurrences: card.occurrences!.sort((a, b) => a.page - b.page),
      // Accumulated across the card's matches, and recorded twice on purpose:
      // `redactedMatchIds` is the state, `autoRedactedMatchIds` is who decided
      // it. The second cannot be derived from the first once a reviewer has
      // touched the document.
      ...(card.autoRedact
        ? {
            redactedMatchIds: [
              ...(existing?.redactedMatchIds ?? []),
              ...card.added,
            ],
            autoRedactedMatchIds: [
              ...(existing?.autoRedactedMatchIds ?? []),
              ...card.added,
            ],
          }
        : {}),
    });
  }

  /*
   * AUTO REDACT IS THREE FIELDS, NOT ONE, and setting only the first left the
   * card in a state the rest of the app reads as contradictory.
   *
   * `redactedMatchIds` says which matches are burned in — `buildRedactionPlan`
   * emits from it, so the export was right. But the PANEL reads `checked` and
   * `checkedPages`, and the Redactions list reads `addedToRedactions`, and
   * neither was set: the matches were redacted in the data and invisible as
   * redactions in the UI.
   *
   * Worse, the card was then unclickable in the obvious way. `TOGGLE_DETECTION`
   * asks "is every live match already redacted?" and inverts — so a reviewer
   * pressing the card's own control found it UN-redacting everything, and
   * `addedToRedactions` was never set because that path only sets it when the
   * result is checked. Both reported symptoms — nothing promoted, and manual
   * adds doing nothing — are this one omission.
   *
   * Derived here rather than in the loop because the derivation needs the
   * card's FINAL occurrence set, and the card is rewritten once per match.
   */
  const runAutoRedactedMatchIds: string[] = [];
  for (const detectionId of autoRedactedCardIds) {
    const card = cards.get(detectionId);
    if (!card) {
      continue;
    }
    // Collected from the cards this run CREATED, where the field can only hold
    // what this run put in it — an existing card is never seeded, so there is
    // no earlier run's marker mixed in here.
    runAutoRedactedMatchIds.push(...(card.autoRedactedMatchIds ?? []));
    const derived = deriveDetectionRedaction(card, card.redactedMatchIds ?? []);
    cards.set(detectionId, {
      ...card,
      redactedMatchIds: derived.redactedMatchIds,
      checkedPages: derived.checkedPages,
      checked: derived.checked,
      // Same rule the manual path follows: anything that redacts a detection
      // also promotes it into the Redactions list.
      addedToRedactions: true,
    });
  }

  // Carry every existing detection through, replacing only the lexicon cards
  // this merge actually touched. PII detections are never rewritten.
  const merged: Detection[] = existingDetections.map((detection) => {
    if (detection.source !== 'lexicon') {
      return detection;
    }
    return cards.get(detection.id) ?? detection;
  });

  for (const [id, card] of cards) {
    if (!existingLexiconCards.has(id)) {
      merged.push(card);
    }
  }

  return {
    detections: merged,
    foldedRedactionIds,
    foldedMatchIds,
    unattributedEntryIds: [...unattributedEntryIds],
    representedMatchCount,
    documentMatchCount: countLexiconMatches(merged),
    skippedDismissedMatchIds,
    alreadyPresentMatchIds,
    runAutoRedactedMatchIds,
  };
}
